// HelpAssistant/useContentAvoidance.js
//
// T22.581: keep the phone edge tab off the page's controls and text. See
// contentAvoidance.js for the measured defect and the geometry.
//
// Re-measures after the page settles: on mount, after scrolling stops
// (any scroller, so capture-phase), on resize and when the DOM changes
// (lists load late). Every trigger is debounced, so a scroll costs one sample
// pass (~3 x 200 elementsFromPoint calls) after it ends, not one per frame.

import { useEffect, useState } from "react";
import { pickClearTop, sampleBusyIntervals } from "./contentAvoidance";

/** Wait this long after the last scroll / resize / DOM change, ms. */
export const SETTLE_MS = 160;

export const LAUNCHER_ROOT_SELECTOR = '[data-testid="help-fab-root"]';

/**
 * `hold` (review-1008-Frontend-7665): while the panel is open the tab keeps
 * the last spot it moved to instead of snapping back to `position.top`.
 * Measured live (web-stg main.0379f12d.js, 384x832): without it a tap on "?"
 * at top 506 sent the tab to 600, away from the finger and back onto the
 * control it had avoided, and the panel opened 94px lower than tapped.
 *
 * @returns {number|null} the top to render at, or null to keep `position.top`.
 */
export default function useContentAvoidance({
  enabled,
  hold = false,
  position,
  box,
  viewport,
  minTop,
  maxTop,
}) {
  const [top, setTop] = useState(null);
  const { width: boxWidth, height: boxHeight } = box;
  const { width: vw, offsetTop = 0 } = viewport;
  const preferredTop = position.top;

  useEffect(() => {
    // Keep the last measured top as is; nothing moves under an open panel.
    if (hold) return undefined;
    if (!enabled || typeof window === "undefined" || typeof document === "undefined") {
      setTop(null);
      return undefined;
    }
    let timer = null;
    const measure = () => {
      timer = null;
      const busy = sampleBusyIntervals({
        doc: document,
        viewportWidth: vw,
        width: boxWidth,
        yMin: minTop + offsetTop,
        yMax: maxTop + boxHeight + offsetTop,
        ignore: document.querySelector(LAUNCHER_ROOT_SELECTOR),
      }).map(([a, b]) => [a - offsetTop, b - offsetTop]);
      const next = pickClearTop({
        preferredTop,
        minTop,
        maxTop,
        height: boxHeight,
        busy,
      });
      setTop(next === preferredTop ? null : next);
    };
    const schedule = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(measure, SETTLE_MS);
    };
    measure();
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    let observer = null;
    if (typeof MutationObserver !== "undefined" && document.body) {
      observer = new MutationObserver((records) => {
        const root = document.querySelector(LAUNCHER_ROOT_SELECTOR);
        // The tab's own re-render must not re-trigger a measure.
        if (root && records.every((r) => root.contains(r.target))) return;
        schedule();
      });
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      if (observer) observer.disconnect();
    };
  }, [enabled, hold, preferredTop, minTop, maxTop, boxWidth, boxHeight, vw, offsetTop]);

  return enabled || hold ? top : null;
}
